import { useEffect, useState } from "react";
import readXlsxFile from "read-excel-file";
import Button from "./Button";

interface FileViewProps {
  files: File;
  onBack: () => void;
  onProcessFile: () => void;
  loading: boolean;
}

interface RowIssue {
  missingFields: string[];
  invalidDates: { field: string; reason: string }[];
  invalidPhones: { field: string; reason: string }[];
  invalidRecordType: { field: string; reason: string }[];
  invalidCountryCode: { field: string; reason: string }[];
}

const REQUIRED_HEADERS = [
  "invoice_number",
  "contract_purchase_date",
  "customer_first_name",
  "customer_last_name",
  "customer_street_address_1",
  "customer_city",
  "customer_state",
  "customer_postal_code",
  "customer_country_code",
  "customer_primary_telephone",
  "contract_retail_price",
  "total_product_retail_value",
  "manufacturer",
  "model_number",
  "purchase_date_of_product",
  "product_retail_price",
  "product_sku",
  "quantity",
  "record_type",
  "selling_store_location",
  "protectall_sku",
];

const FileViewComponent = ({
  files,
  onBack,
  onProcessFile,
  loading,
}: FileViewProps) => {
  const [data, setData] = useState<any[][]>([]);
  const [missingHeaders, setMissingHeaders] = useState<string[]>([]);
  const [fileHasOverallErrors, setFileHasOverallErrors] = useState(false);
  const [rowErrors, setRowErrors] = useState<Array<RowIssue | null>>([]);

  let tempRowIndex = 0;

  useEffect(() => {
    if (!files) return;

    readXlsxFile(files)
      .then((rows) => {
        setData(rows);
        const headers =
          rows[0]?.map((h: any) => h?.toString().trim().toLowerCase()) || [];
        const missing = REQUIRED_HEADERS.filter(
          (required) => !headers.includes(required.toLowerCase())
        );
        setMissingHeaders(missing);
        setFileHasOverallErrors(missing.length > 0);
        if (missing.length > 0) {
          setRowErrors([]);
        }
      })
      .catch((err) => {
        console.error("Error reading file", err);
        setData([]);
        setMissingHeaders([]);
        setFileHasOverallErrors(true);
        setRowErrors([]);
      });
  }, [files]);

  const isValidDate = (value: any): boolean => {
    const date = new Date(value);
    return (
      value instanceof Date ||
      (!isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}(T|$)/.test(String(value)))
    );
  };

  useEffect(() => {
    if (data.length <= 1 || missingHeaders.length > 0) {
      setRowErrors([]);
      if (missingHeaders.length > 0) {
        setFileHasOverallErrors(true);
      }
      return;
    }

    const headerRow = data[0];
    const errors: Array<RowIssue | null> = [];
    let overallErrorsFound = false;

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const rowIssue: RowIssue = {
        missingFields: [],
        invalidDates: [],
        invalidPhones: [],
        invalidRecordType: [],
        invalidCountryCode: [],
      };

      for (const required of REQUIRED_HEADERS) {
        const index = headerRow.findIndex(
          (h: any) =>
            h?.toString().trim().toLowerCase() === required.toLowerCase()
        );

        if (index === -1) continue;

        const value = row[index];

        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ""
        ) {
          rowIssue.missingFields.push(required);
        } else {
          if (required.toLowerCase().includes("date")) {
            if (!isValidDate(value)) {
              rowIssue.invalidDates.push({
                field: required,
                reason: "Invalid date format (YYYY-MM-DD expected)",
              });
            }
          }

          if (required.toLowerCase().includes("phone")) {
            const cleaned = String(value).replace(/\D/g, "");
            if (cleaned.length !== 10 || !/^\d+$/.test(cleaned)) {
              rowIssue.invalidPhones.push({
                field: required,
                reason: `Invalid phone format (10 digits expected)`,
              });
            }
          }

          if (
            required.toLowerCase() === "record_type" &&
            !["S", "C"].includes(String(value).trim())
          ) {
            rowIssue.invalidRecordType.push({
              field: required,
              reason: `Allowed values are 'S' or 'C'`,
            });
          }

          if (
            required.toLowerCase() === "customer_country_code" &&
            String(value).trim().length !== 3
          ) {
            rowIssue.invalidCountryCode.push({
              field: required,
              reason: `Must be exactly 3 characters`,
            });
          }
        }
      }

      if (
        rowIssue.missingFields.length > 0 ||
        rowIssue.invalidDates.length > 0 ||
        rowIssue.invalidPhones.length > 0 ||
        rowIssue.invalidRecordType.length > 0 ||
        rowIssue.invalidCountryCode.length > 0
      ) {
        errors.push(rowIssue);
        overallErrorsFound = true;
      } else {
        errors.push(null);
      }
    }
    setRowErrors(errors);
    setFileHasOverallErrors((prev) => prev || overallErrorsFound);
  }, [data, missingHeaders]);

  const isButtonDisabled = fileHasOverallErrors || data.length <= 1;

  const getDisabledReason = () => {
    if (missingHeaders.length > 0)
      return `The file is missing required columns: ${missingHeaders.join(", ")}`;
    if (data.length <= 1) return "No data rows found in the Excel file.";
    if (fileHasOverallErrors)
      return "There are errors in the data rows. Please fix them in your source file and re-upload before processing.";
    return "";
  };

  const formatCell = (cell: any): string => {
    if (cell instanceof Date) return cell.toLocaleDateString();
    if (cell === null || cell === undefined) return "";
    return String(cell);
  };

  if (!files) return null;
  return (
    <div className="space-y-2 p-2">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-b pb-4">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <div className="truncate min-w-0 max-w-xs">{files.name}</div>
        </h2>
        <div className="flex gap-3 items-center">
          <div
            className={`relative group ${isButtonDisabled ? "cursor-pointer" : ""}`}
          >
            <Button
              btnClassName="pr-5 pl-5"
              variant="blue"
              onClick={onProcessFile}
              isLoading={loading}
              disabled={loading || isButtonDisabled}
            >
              {loading ? "Processing..." : "Process File"}
            </Button>
            {isButtonDisabled && !loading && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 hidden group-hover:block bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-2xl w-64 z-[60] text-center pointer-events-none transition-opacity duration-200 after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-gray-900">
                {getDisabledReason()}
              </div>
            )}
          </div>
          <Button variant="gray" onClick={onBack} btnClassName="pr-5 pl-5">
            Back
          </Button>
        </div>
      </div>

      {missingHeaders.length > 0 && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <p className="font-bold">
            Please ensure your Excel file contains all necessary columns.
          </p>
          <p className="mt-2 text-sm">
            <span className="font-semibold">Missing columns:</span>{" "}
            {missingHeaders.join(", ")}
          </p>
        </div>
      )}
      {fileHasOverallErrors && !(missingHeaders.length > 0) && (
        <div
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4"
          role="alert"
        >
          <p>
            Invalid records are highlighted in red. Please correct them in your
            source file and re-upload to proceed.
          </p>
        </div>
      )}

      {data.length > 0 ? (
        <div className="overflow-auto max-h-[70vh] border border-gray-200 rounded-md">
          <table className="min-w-full text-sm text-left border-separate border-spacing-0">
            <thead className="sticky top-0 bg-zinc-500 z-10">
              <tr>
                <th
                  className={`px-4 py-3 font-semibold sticky left-0 z-20  ${
                    missingHeaders.length > 0
                      ? "text-red-700 bg-red-100 border-red-300 border-r border-t border-b border-l"
                      : "text-white bg-zinc-500 border-zinc-400 border-r border-t border-b"
                  } w-12 text-center`}
                >
                  #
                </th>
                {data[0].map((cell: any, i: number) => (
                  <th
                    key={i}
                    className={`px-4 py-3 font-semibold z-20   ${
                      missingHeaders.length > 0
                        ? "text-red-700 bg-red-100 border-red-300 border-r border-t border-b"
                        : "text-white bg-zinc-500 border-zinc-400 border-r border-t border-b"
                    } w-12 text-center`}
                  >
                    {formatCell(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(1).map((row: any[], i: number) => {
                // const rowIndex = i + 1;
                const errorsForThisRow =
                  missingHeaders.length === 0 ? rowErrors[i] : null;
                const hasIssues = errorsForThisRow !== null;
                if (!hasIssues) {
                  tempRowIndex = tempRowIndex + 1;
                }

                return (
                  <tr key={i} className={`bg-white text-black`}>
                    <td
                      className={`border-r  border-b px-4 py-2 text-center sticky left-0 ${
                        hasIssues ? "text-red-600 bg-red-50" : "bg-white"
                      }`}
                    >
                      <div className="flex items-center justify-center h-full">
                        {!hasIssues && tempRowIndex}
                        {hasIssues && (
                          <div className="relative group">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="red"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="cursor-pointer"
                            >
                              <path
                                stroke="none"
                                d="M0 0h24v24H0z"
                                fill="none"
                              />
                              <path d="M12 9h.01" />
                              <path d="M11 12h1v4h1" />
                              <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
                            </svg>
                            <div className="absolute z-20 hidden group-hover:block bg-white text-red-700 text-xs p-3 border border-red-300 rounded-lg shadow-lg w-72 left-8 top-1/2 -translate-y-1/2 before:content-[''] before:absolute before:left-[-8px] before:top-1/2 before:-translate-y-1/2 before:w-0 before:h-0 before:border-t-8 before:border-b-8 before:border-r-8 before:border-transparent before:border-r-red-300">
                              {errorsForThisRow?.missingFields.length > 0 && (
                                <div className="text-left mb-1">
                                  <strong className="block">
                                    Missing Fields:
                                  </strong>{" "}
                                  {errorsForThisRow.missingFields.join(", ")}
                                </div>
                              )}
                              {errorsForThisRow?.invalidDates.length > 0 && (
                                <div className="text-left mb-1">
                                  <strong className="block">
                                    Invalid Dates:
                                  </strong>{" "}
                                  {errorsForThisRow.invalidDates
                                    .map((e: any) => `${e.field} (${e.reason})`)
                                    .join(", ")}
                                </div>
                              )}
                              {errorsForThisRow?.invalidPhones.length > 0 && (
                                <div className="text-left mb-1">
                                  <strong className="block">
                                    Invalid Phones:
                                  </strong>{" "}
                                  {errorsForThisRow.invalidPhones
                                    .map((e: any) => `${e.field} (${e.reason})`)
                                    .join(", ")}
                                </div>
                              )}
                              {errorsForThisRow?.invalidRecordType.length >
                                0 && (
                                <div className="text-left mb-1">
                                  <strong className="block">
                                    Invalid Record Type:
                                  </strong>{" "}
                                  {errorsForThisRow.invalidRecordType
                                    .map((e: any) => `${e.field} (${e.reason})`)
                                    .join(", ")}
                                </div>
                              )}
                              {errorsForThisRow?.invalidCountryCode.length >
                                0 && (
                                <div className="text-left">
                                  <strong className="block">
                                    Invalid Country Code:
                                  </strong>{" "}
                                  {errorsForThisRow.invalidCountryCode
                                    .map((e: any) => `${e.field} (${e.reason})`)
                                    .join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                    {row.map((cell: any, j: number) => {
                      const headerName = data[0][j]
                        ?.toString()
                        .trim()
                        .toLowerCase();
                      const isCellMissing =
                        errorsForThisRow?.missingFields.includes(headerName);
                      const isCellInvalidDate =
                        errorsForThisRow?.invalidDates.some(
                          (e) => e.field.toLowerCase() === headerName
                        );
                      const isCellInvalidPhone =
                        errorsForThisRow?.invalidPhones.some(
                          (e) => e.field.toLowerCase() === headerName
                        );
                      const isCellInvalidRecordType =
                        errorsForThisRow?.invalidRecordType.some(
                          (e) => e.field.toLowerCase() === headerName
                        );
                      const isCellInvalidCountryCode =
                        errorsForThisRow?.invalidCountryCode.some(
                          (e) => e.field.toLowerCase() === headerName
                        );

                      const hasCellError =
                        isCellMissing ||
                        isCellInvalidDate ||
                        isCellInvalidPhone ||
                        isCellInvalidRecordType ||
                        isCellInvalidCountryCode;
                      return (
                        <td
                          key={j}
                          className={`border-r border-b px-4 py-2 ${
                            hasCellError ? "bg-red-50 text-red-700" : ""
                          }`}
                        >
                          {formatCell(cell)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center text-gray-500 py-8">
          No data to display. Please ensure your Excel file is correctly
          formatted.
        </p>
      )}
    </div>
  );
};

export default FileViewComponent;
